/**
 * How a document's state is drawn, and in what order the filters offer it.
 *
 * Here rather than in each screen because the ledger, the document and the
 * statement all render the same badge, and the one that mattered is `overdue`:
 * it was drawn as a neutral tone on one screen and a warning on another, so
 * the same invoice looked urgent to the ADV and routine to the customer.
 *
 * `partial` is a warning rather than a success even though money has arrived.
 * Something is still owed, and a green badge on a half-paid invoice reads as
 * settled at a glance — which is the glance that matters in a list of fifty.
 */

import type { BadgeTone, StatusTone } from "@hardauto/core-ui";

import type { DocumentStatus, PaymentMethod } from "./api";

export const STATUS_TONE: Record<DocumentStatus, BadgeTone> = {
  paid: "success",
  open: "neutral",
  overdue: "danger",
  partial: "warning",
  credit: "info",
};

/** The order the filters and the legend use: what needs attention first. */
export const STATUSES: readonly DocumentStatus[] = ["overdue", "partial", "open", "paid", "credit"];

export const METHODS: readonly PaymentMethod[] = [
  "transfer",
  "cheque",
  "cash",
  "card",
  "credit_note",
  "other",
];

/**
 * How full the credit line is drawn.
 *
 * The thresholds are the ones the sales desk already works to: past 85 % a
 * customer is on the watch list, and at 100 % the next order is held. Drawing
 * the bar green up to the moment it blocks would make the block a surprise.
 */
export function creditTone(usedPct: number): StatusTone {
  if (usedPct >= 100) return "danger";
  if (usedPct >= 85) return "warning";
  return "accent";
}
